import { makeAutoObservable, runInAction } from 'mobx'

import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'

import type Service from '.'
import type { RawWorkflowScheduleDefinition } from './model/workflow/WorkflowScheduleDefinitionModel'
import WorkflowScheduleDefinitionModel from './model/workflow/WorkflowScheduleDefinitionModel'
import { SCHEDULE_VARIABLE_KEYS } from './workflowScheduleConstants'

export default class WorkflowScheduleStore {
  // We keep track of the entity ids for which we have fetched schedules
  private readonly fetchedSchedulesEntityIds = new Set<string>()
  // Map of schedules by schedule ID
  private readonly schedulesById = new Map<string, WorkflowScheduleDefinitionModel>()

  private readonly disposeBag = new DisposeBag()

  constructor(private root: Service) {
    makeAutoObservable(this)

    this.disposeBag.add(this.subscribeToWebSocket())
  }

  getSchedulesForEntityId(entityId: string): WorkflowScheduleDefinitionModel[] {
    const localSchedulesForEntityId = Array.from(this.schedulesById.values()).filter(
      (schedule) => schedule.entityId === entityId,
    )

    void this.fetchSchedulesForEntityIdIfNeeded(entityId)

    return localSchedulesForEntityId
  }

  private async fetchSchedulesForEntityIdIfNeeded(entityId: string) {
    if (this.fetchedSchedulesEntityIds.has(entityId)) {
      return
    }

    const schedules = await this.fetchSchedulesForEntityId(entityId)
    runInAction(() => {
      this.fetchedSchedulesEntityIds.add(entityId)
      schedules.forEach((schedule) => {
        this.schedulesById.set(schedule.id, new WorkflowScheduleDefinitionModel(schedule))
      })
    })
  }

  private async fetchSchedulesForEntityId(entityId: string) {
    const data: RawWorkflowScheduleDefinition[] = []
    let pageToken: string | undefined
    do {
      const response = await this.root.transport.workflow.schedules.list({
        orgId: this.root.organization.getCurrentOrganization().id,
        entityIds: [entityId],
        triggerId: 'WTDscheduledEnableWorkflow',
        maxResults: 50,
        pageToken,
      })
      data.push(...response.data)
      pageToken = response.nextPageToken
    } while (pageToken)

    return data
  }

  getScheduleById(scheduleId: string): WorkflowScheduleDefinitionModel | null {
    return this.schedulesById.get(scheduleId) ?? null
  }

  getSchedulesForWorkflow(
    workflowDefinitionId: string,
  ): WorkflowScheduleDefinitionModel[] {
    return Array.from(this.schedulesById.values()).filter((schedule) => {
      const workflowIdConfig = schedule.configuration?.find(
        (config) => config.variableKey === SCHEDULE_VARIABLE_KEYS.WORKFLOW_DEFINITION_ID,
      )
      return workflowIdConfig?.value === workflowDefinitionId
    })
  }

  async createOrUpdateSchedule({
    entityId,
    workflowDefinitionId,
    scheduledFor,
    scheduleId,
  }: {
    entityId: string
    workflowDefinitionId: string
    scheduledFor: string
    scheduleId?: string
  }): Promise<WorkflowScheduleDefinitionModel> {
    const configuration: RawWorkflowScheduleDefinition['configuration'] = [
      {
        variableKey: SCHEDULE_VARIABLE_KEYS.WORKFLOW_DEFINITION_ID,
        value: workflowDefinitionId,
      },
    ]

    const scheduleData = {
      orgId: this.root.organization.getCurrentOrganization().id,
      entityId,
      trigger: { definitionId: 'WTDscheduledEnableWorkflow' as const },
      workflow: { definitionId: 'WDenableWorkflow' },
      configuration,
      scheduledFor,
    }

    const existingSchedule = scheduleId ? this.getScheduleById(scheduleId) : null

    // Generate a temporary ID for optimistic updates if creating a new schedule
    const optimisticId = existingSchedule?.id ?? `temp-schedule-${Date.now()}`
    const optimisticSchedule: RawWorkflowScheduleDefinition = {
      ...scheduleData,
      id: optimisticId,
      createdAt: existingSchedule?.createdAt ?? new Date().toISOString(),
      version: existingSchedule?.version ?? 1,
      createdBy: existingSchedule?.createdBy ?? this.root.user.getCurrentUser().id,
    }

    // Optimistically update the UI
    const optimisticModel = new WorkflowScheduleDefinitionModel(optimisticSchedule)
    runInAction(() => {
      this.schedulesById.set(optimisticId, optimisticModel)
    })

    try {
      let schedule: RawWorkflowScheduleDefinition

      if (existingSchedule) {
        // Update existing schedule
        schedule = await this.root.transport.workflow.schedules.put(
          existingSchedule.id,
          scheduleData,
        )
      } else {
        // Create new schedule
        schedule = await this.root.transport.workflow.schedules.post(scheduleData)
      }

      // Update with the real schedule from the server
      const scheduleModel = new WorkflowScheduleDefinitionModel(schedule)
      runInAction(() => {
        // Remove the optimistic schedule if we had a temporary ID
        if (!existingSchedule) {
          this.schedulesById.delete(optimisticId)
        }
        this.schedulesById.set(schedule.id, scheduleModel)
      })

      return scheduleModel
    } catch (error) {
      // Revert the optimistic update
      runInAction(() => {
        if (existingSchedule) {
          // Restore the original schedule
          this.schedulesById.set(existingSchedule.id, existingSchedule)
        } else {
          // Remove the optimistic schedule
          this.schedulesById.delete(optimisticId)
        }
      })

      throw error
    }
  }

  // Deletes a workflow schedule
  async deleteSchedule(scheduleId: string): Promise<void> {
    // Store the schedule for potential rollback
    const scheduleToDelete = this.getScheduleById(scheduleId)

    if (!scheduleToDelete) {
      return
    }

    // Optimistically remove from cache
    runInAction(() => {
      this.schedulesById.delete(scheduleId)
    })

    try {
      await this.root.transport.workflow.schedules.delete(scheduleId)
    } catch (error) {
      // Revert the optimistic delete by restoring the schedule
      runInAction(() => {
        this.schedulesById.set(scheduleId, scheduleToDelete)
      })

      throw error
    }
  }

  private subscribeToWebSocket() {
    return this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'workflow-schedule-definition-create':
        case 'workflow-schedule-definition-update': {
          this.schedulesById.set(
            data.definition.id,
            new WorkflowScheduleDefinitionModel(data.definition),
          )
          break
        }
        case 'workflow-schedule-definition-delete': {
          this.schedulesById.delete(data.id)
          break
        }
      }
    })
  }
}
